import React, { useState, useEffect, useRef } from 'react';
import { AiAgentAvatar, isAiAgentId } from '../atoms/ai-agent-avatar';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Button, ButtonVariant, ButtonSize } from '../atoms/button';
import { Card, CardVariant, CardPadding } from '../atoms/card';
import { Avatar, AvatarSize, AvatarVariant, AvatarStatus } from '../atoms/avatar';
import TextContainer, { TagVariant, TagSize } from '../atoms/text-container';
import HelpPopup from './HelpPopup';


const StyledCard = styled(Card)`
  /* Any additional specific styling for ExpertCard can go here */
`;

const ExpertInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
`;

const ExpertDetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  margin-right: 16px;
`;



// "AI advisor" / "AI agent · <owner>" pill, as on the website's advisor cards
const AdvisorTypePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  margin: 2px 0 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  color: #7955cd;
  background: #f0ecff;
`;

const ExpertNameLocationWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExpertName = styled.div`
  font-weight: 600;
  font-size: 1.25em;
  color: #192947;
  margin-bottom: 4px;
`;

const ExpertStatus = styled.div`
  display: flex;
  align-items: center;
  color: ${props => {
    if (props.$status === 'Finished') return '#0f8f63';
    if (props.$status === 'Awaiting Contact') return '#29a9dd';
    if (props.$status.includes('Awaiting')) return '#ef9b61';
    return '#12a571';
  }};
  border: ${props => {
    if (props.$status === 'Finished') return '1px solid #dce5f4';
    if (props.$status === 'Awaiting Contact') return '1px solid rgba(41, 169, 221, 0.3)';
    if (props.$status.includes('Awaiting')) return '1px solid rgba(239, 155, 97, 0.3)';
    return '1px solid rgba(110, 200, 180, 0.3)';
  }};
  background: ${props => {
    if (props.$status === 'Finished') return 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%)';
    if (props.$status === 'Awaiting Contact') return 'linear-gradient(135deg, rgba(243, 245, 249, 0.92) 0%, rgba(243, 245, 250, 0.92) 100%)';
    if (props.$status.includes('Awaiting')) return '#fff5e9';
    return 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%)';
  }};
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.8em;
  font-weight: 500;
  width: auto;
  justify-content: center;
  backdrop-filter: blur(5px);
  box-shadow: 0 1px 3px rgba(39, 68, 131, 0.035);
`;

const ExpertContract = styled.div`
  font-size: 0.925em;
  margin: 0;
  color: #455776;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1; /* Take up available space */
  
  strong {
    color: #192947;
    font-weight: 500;
  }
`;

const MeetingSection = styled.div`
  padding: 10px;
  background: #f5f8fd;
  border-radius: 8px;
  border: 1px solid #e0e8f5;
  min-height: ${props => props.$noMeeting ? '80px' : 'auto'}; /* Ensure minimum height when no meeting */
  margin-bottom: 8px;
`;

const MeetingHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const HeaderLine = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  
  &.with-badge {
    justify-content: space-between;
    margin-top: 0;
  }
  
  &.title {
    font-weight: 500;
    color: #33445e;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(to right, #f5f8fd, #e6ecf6, #f5f8fd);
  margin: 8px 0;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 8px; /* Consistent spacing before buttons */
  width: 100%;
`;

const StyledButton = styled(Button)`
  flex: 1;
  font-size: 0.925em;
`;

/* A written consultation from imavenai.com that belongs to this conversation (Home maps them). */
const ConsultationHint = styled.div`
  margin-top: 8px;
  font-size: 0.85em;
  color: #65748d;
  text-align: center;
`;

const MeetingDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const MeetingDetailLine = styled.div`
  display: flex;
  font-size: 0.95em;
  line-height: 1.3;
`;

// Help Button Styled Components
const HelpButtonContainer = styled.div`
  display: none;
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 999999999; /* Very high z-index */
`;

const HelpButton = styled(Button)`
  width: 30px;
  height: 30px;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(39, 68, 131, 0.105);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 68, 131, 0.14);
  }
`;


const ExpertCardComponent = ({ expert, onScheduleMeeting, isMettingScheduled, consultation, onReadConsultation }) => {
    const navigate = useNavigate();
    const [showHelp, setShowHelp] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
    const [isMobile, setIsMobile] = useState(false);

    // Add reference for portal container and the help button
    const portalRef = useRef(null);
    const helpButtonRef = useRef(null);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Initial check
        checkMobile();

        // Add resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Create portal container when component mounts
    useEffect(() => {
        // Create portal container if it doesn't exist
        if (!portalRef.current) {
            portalRef.current = document.createElement('div');
            portalRef.current.className = 'help-portal-container';
            document.body.appendChild(portalRef.current);
        }

        // Clean up when component unmounts
        return () => {
            if (portalRef.current && portalRef.current.parentNode) {
                portalRef.current.parentNode.removeChild(portalRef.current);
                portalRef.current = null;
            }
        };
    }, []);

    // Debug log when popupPosition changes
    useEffect(() => {
        console.log(`[Expert ${expert.name}] popupPosition updated:`, popupPosition);
    }, [popupPosition, expert.name]);


    // Function to update popup position based on button position
    const updatePopupPosition = () => {
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const maxHeight = viewportHeight * 0.8; // 80% of viewport height

        console.log(`[Expert ${expert.name}] updatePopupPosition called`);
        console.log(`Viewport dimensions: ${viewportWidth}x${viewportHeight}`);
        console.log(`Is Mobile: ${isMobile}`);

        if (isMobile) {
            // For mobile, position popup in center of screen for better visibility
            // with space at both top and bottom for scrolling
            const topOffset = Math.max(60, (viewportHeight - maxHeight) / 2);
            console.log(`[Mobile] Setting popup position - top: ${topOffset}, right: 10`);
            setPopupPosition({
                top: topOffset,
                right: 10
            });
        } else if (helpButtonRef.current) {
            // For desktop, position relative to the button
            const buttonRect = helpButtonRef.current.getBoundingClientRect();
            console.log(`[Desktop] Button rect:`, buttonRect);
            console.log(`Button position - top: ${buttonRect.top}, left: ${buttonRect.left}, right: ${buttonRect.right}, bottom: ${buttonRect.bottom}`);
            
            const popupWidth = 320; // Width of the popup as defined in HelpPopup
            const estimatedPopupHeight = Math.min(500, maxHeight);
            
            // Calculate position to show popup to the left of the button
            let rightPosition = viewportWidth - buttonRect.left + 10; // 10px gap from button
            let topPosition = buttonRect.top;
            
            console.log(`Initial calculation - top: ${topPosition}, right: ${rightPosition}`);
            console.log(`Popup would extend to x: ${viewportWidth - rightPosition - popupWidth}`);
            
            // Check if popup would go off the left edge of viewport
            if (rightPosition > viewportWidth - popupWidth - 20) {
                console.log(`Not enough space on left, repositioning below button`);
                // If not enough space on the left, show it below the button instead
                rightPosition = viewportWidth - buttonRect.right;
                topPosition = buttonRect.bottom + 10;
            }
            
            // Check if popup would extend beyond the bottom of the viewport
            if (topPosition + estimatedPopupHeight > viewportHeight - 20) {
                const newTop = Math.max(20, viewportHeight - estimatedPopupHeight - 20);
                console.log(`Popup would extend beyond bottom, adjusting top from ${topPosition} to ${newTop}`);
                topPosition = newTop;
            }
            
            // Ensure popup doesn't go above the top of the viewport
            topPosition = Math.max(20, topPosition);

            console.log(`[Desktop] Final popup position - top: ${topPosition}, right: ${rightPosition}`);
            setPopupPosition({
                top: topPosition,
                right: rightPosition
            });
        } else {
            console.log(`[Error] helpButtonRef.current is null`);
        }
    };

    // Toggle help and update position
    const toggleHelp = (event) => {
        console.log(`[Expert ${expert.name}] toggleHelp called, current showHelp: ${showHelp}`);
        
        if (!showHelp && event && event.currentTarget) {
            // Get button position from event target
            const buttonRect = event.currentTarget.getBoundingClientRect();
            console.log(`[Expert ${expert.name}] Button rect from event:`, buttonRect);
            
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const popupWidth = 320;
            const estimatedPopupHeight = Math.min(500, viewportHeight * 0.8);
            
            // Calculate position to show popup to the left of the button
            let rightPosition = viewportWidth - buttonRect.left + 10;
            let topPosition = buttonRect.top;
            
            // Check if popup would go off the left edge of viewport
            if (rightPosition > viewportWidth - popupWidth - 20) {
                rightPosition = viewportWidth - buttonRect.right;
                topPosition = buttonRect.bottom + 10;
            }
            
            // Check if popup would extend beyond the bottom of the viewport
            if (topPosition + estimatedPopupHeight > viewportHeight - 20) {
                topPosition = Math.max(20, viewportHeight - estimatedPopupHeight - 20);
            }
            
            topPosition = Math.max(20, topPosition);
            
            console.log(`[Expert ${expert.name}] Setting popup position - top: ${topPosition}, right: ${rightPosition}`);
            setPopupPosition({
                top: topPosition,
                right: rightPosition
            });
        }
        
        setShowHelp(!showHelp);
        console.log(`[Expert ${expert.name}] showHelp set to: ${!showHelp}`);
    };

    // Update position if window resizes
    useEffect(() => {
        if (showHelp) {
            const handleResize = () => {
                updatePopupPosition();
            };

            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [showHelp]);

    // Add click outside handler for each card's help popup
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if it's a touch event within the popup (for scrolling on mobile)
            if (event.type === 'touchend' && event.target.closest(`.help-popup-${expert.id}`)) {
                return;
            }

            if (showHelp &&
                !event.target.closest(`.help-popup-${expert.id}`) &&
                !event.target.closest(`.help-button-${expert.id}`)) {
                setShowHelp(false);
            }
        };

        // Add a special handler for the help popup to prevent scroll events from closing
        const handlePopupInteraction = (event) => {
            // If it's a scroll event within the popup, stop it from bubbling
            if (event.type === 'scroll' || event.type === 'wheel' ||
                event.type === 'touchmove' || event.type === 'touchstart') {
                event.stopPropagation();
            }
        };

        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touchend', handleClickOutside);

        // Add event listeners to the help popup if it exists
        const popupElement = document.querySelector(`.help-popup-${expert.id}`);
        if (popupElement) {
            popupElement.addEventListener('scroll', handlePopupInteraction);
            popupElement.addEventListener('wheel', handlePopupInteraction);
            popupElement.addEventListener('touchmove', handlePopupInteraction);
            popupElement.addEventListener('touchstart', handlePopupInteraction);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('touchend', handleClickOutside);

            // Clean up event listeners
            const popupElement = document.querySelector(`.help-popup-${expert.id}`);
            if (popupElement) {
                popupElement.removeEventListener('scroll', handlePopupInteraction);
                popupElement.removeEventListener('wheel', handlePopupInteraction);
                popupElement.removeEventListener('touchmove', handlePopupInteraction);
                popupElement.removeEventListener('touchstart', handlePopupInteraction);
            }
        };
    }, [showHelp, expert.id]);


    const handleNavigateToChat = () => {
        navigate(`/conversations/${expert.threadId}`);
    };

    const formatToDate = (dateStr) => {
        const date = new Date(dateStr);
        const monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        const month = monthNames[date.getUTCMonth()];
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();
        return `${month} ${day}, ${year}`;
    }

    // Format meeting time if available
    const formatMeetingTime = (meetingInfo) => {
        if (!meetingInfo) return null;

        try {
            // Check if we have the new format with startTime and endTime fields
            if (meetingInfo.startTime && meetingInfo.endTime) {
                const startDate = new Date(meetingInfo.startTime);
                const endDate = new Date(meetingInfo.endTime);

                // Format date
                const formattedDate = formatToDate(startDate);

                // Format times
                const formatTimeFromDate = (date) => {
                    const hours = date.getHours();
                    const minutes = date.getMinutes();
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const formattedHours = hours % 12 || 12;
                    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
                    return `${formattedHours}:${formattedMinutes} ${ampm}`;
                };

                const startTime = formatTimeFromDate(startDate);
                const endTime = formatTimeFromDate(endDate);

                return {
                    date: formattedDate,
                    startTime: startTime,
                    endTime: endTime,
                    full: `${formattedDate} from ${startTime} to ${endTime}`
                };
            }

            // Fallback for old format
            if (meetingInfo.date) {
                const date = new Date(meetingInfo.date);
                const formattedDate = formatToDate(meetingInfo.date);

                // Format time (assuming meetingInfo.time exists or use hour/minute from date)
                let timeStr = meetingInfo.time;
                if (!timeStr) {
                    const hours = date.getHours();
                    const minutes = date.getMinutes();
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const formattedHours = hours % 12 || 12;
                    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
                    timeStr = `${formattedHours}:${formattedMinutes} ${ampm}`;
                }

                return {
                    date: formattedDate,
                    startTime: timeStr,
                    full: `${formattedDate} at ${timeStr}`
                };
            }

            return null;
        } catch (error) {
            console.error('Error formatting meeting time:', error);
            return null;
        }
    };

    // Who is behind this card: a person, a pure AI advisor (nobody to meet), or an AI agent
    // published by a person (meetings are with that owner). Older servers send no advisorType.
    const advisorType = expert.advisorType
        || (String(expert.userId || '').startsWith('template|') ? 'ai' : 'human');
    const isPureAI = advisorType === 'ai';
    const hostName = expert.meetingHost?.name;
    const canScheduleMeeting = expert.canScheduleMeeting
        ?? (!isPureAI && expert.meetingSettings?.allowManualMeetings !== false);

    // Determine the status to display
    const rawStatus = expert.status || "Started";
    
    // Debug log to trace the meetingSettings
    console.log(`[ExpertCard] ${expert.name} - meetingSettings:`, expert.meetingSettings, 
        `isMettingScheduled:`, isMettingScheduled, 
        `rawStatus:`, rawStatus);
    
    // Determine display status based on meeting settings and meeting scheduled state
    let displayStatus;
    if (rawStatus === "Started" && !isPureAI) {
        if (isMettingScheduled) {
            displayStatus = "Awaiting Meeting";
        } else if (expert.meetingSettings?.allowMeetingsFromWidget === false || 
                   expert.meetingSettings?.allowManualMeetings === false) {
            // Meetings are disabled - show waiting for contact
            displayStatus = "Awaiting Contact";
        } else {
            displayStatus = rawStatus;
        }
    } else {
        displayStatus = rawStatus;
    }
    
    // Debug log the final displayStatus
    console.log(`[ExpertCard] ${expert.name} - Final displayStatus:`, displayStatus);

    // Ensure isOnline is treated strictly as a boolean
    const isOnline = Boolean(expert.isOnline);

    // Get meeting info if available
    const meetingDateTime = formatMeetingTime(expert.meetingInfo);

    // Get categories or fallback to a default category
    const userCategories = expert.userCategories
        ? (typeof expert.userCategories === 'string'
            ? (expert.userCategories.trim() === '' ? ['Expert Adviser'] : [expert.userCategories]) // Handle empty string
            : Array.isArray(expert.userCategories)
                ? (expert.userCategories.length === 0 ? ['Expert Adviser'] : expert.userCategories) // Handle empty array
                : ['Expert Adviser']) // Handle other cases
        : (expert.name && expert.name.toLowerCase().includes('finance')
            ? ['Finance', 'Investment']
            : ['Expert Adviser']);

    // Log for debugging
    console.log(`Rendering expert ${expert.name} with isOnline:`, isOnline);


    return (
        <StyledCard 
            variant={CardVariant.GLASS}
            padding={CardPadding.MD}
            fullHeight={true}
            hover={true}
            className="expert-card angular-component glass-ui"
        >
            {/* Add help button to each card */}
            <HelpButtonContainer>
                <HelpButton
                    ref={helpButtonRef}
                    variant={ButtonVariant.SCHEDULE}
                    size={ButtonSize.SM}
                    round={true}
                    className={`help-button help-button-${expert.id}`}
                    onClick={toggleHelp}
                    style={{ zIndex: 999999999 }}
                >
                    ?
                </HelpButton>
            </HelpButtonContainer>

            {/* Render help popup through HelpPopup organism */}
            <HelpPopup 
                expert={expert}
                userCategories={userCategories}
                displayStatus={displayStatus}
                meetingDateTime={meetingDateTime}
                isMettingScheduled={isMettingScheduled}
                formatToDate={formatToDate}
                showHelp={showHelp}
                popupPosition={popupPosition}
                isMobile={isMobile}
                portalRef={portalRef}
            />

            <ExpertInfo>
                <ExpertDetailsWrapper>
                    <ProfileImageContainer className="profile-image-container">
                        {(isPureAI || isAiAgentId(expert.userId)) ? (
                          <AiAgentAvatar size={56} radius={14} label={`${expert.name} (AI agent)`} className="expert-image profile-image" />
                        ) : (
                        <Avatar
                            size={AvatarSize.EXPERT}
                            variant={AvatarVariant.ROUNDED}
                            src={expert.imageUrl}
                            alt={expert.name}
                            name={expert.name}
                            status={isOnline ? AvatarStatus.ONLINE : AvatarStatus.OFFLINE}
                            withBorder={true}
                            withShadow={true}
                            marginRight="16px"
                            className="expert-image profile-image"
                        />
                        )}
                    </ProfileImageContainer>
                    <ExpertNameLocationWrapper>
                        <ExpertName>{expert.name}</ExpertName>
                        {isPureAI && <AdvisorTypePill>AI advisor</AdvisorTypePill>}
                        {advisorType === 'ai-with-human' && (
                            <AdvisorTypePill>AI agent{hostName ? ` · ${hostName}` : ''}</AdvisorTypePill>
                        )}
                        <TextContainer
                            tags={Array.isArray(userCategories) 
                                ? userCategories 
                                : [expert.location || "Expert Advisor"]
                            }
                            variant={TagVariant.EXPERT}
                            size={TagSize.MD}
                        />
                    </ExpertNameLocationWrapper>
                </ExpertDetailsWrapper>
                {/* OptionsMenu removed */}
            </ExpertInfo>

            <Divider className="expert-card-divider" />

            <ExpertContract className="expert-info-section">
                <MeetingSection $noMeeting={!isMettingScheduled}>
                    <MeetingHeader>
                        <HeaderLine className="title">Advisory Journey Began: {formatToDate(expert.startTime).replace(', ', ' , ')}</HeaderLine>
                        <HeaderLine className="with-badge">
                            Current Session Status:
                            <ExpertStatus
                                $status={displayStatus}
                                className={`expert-status-badge ${displayStatus.toLowerCase().replace(" ", "-")}`}
                            >
                                {displayStatus}
                            </ExpertStatus>
                        </HeaderLine>
                    </MeetingHeader>
                </MeetingSection>

                <MeetingSection $noMeeting={!isMettingScheduled}>
                    {isMettingScheduled
                        ? (meetingDateTime
                            ? <div>
                                <strong>Meeting scheduled with {hostName || expert.name}</strong>
                                <MeetingDetailsSection>
                                    <MeetingDetailLine>
                                        <strong>Meeting details:</strong>
                                    </MeetingDetailLine>
                                    <MeetingDetailLine>
                                        Virtual Meeting: {meetingDateTime.date}
                                    </MeetingDetailLine>
                                    <MeetingDetailLine>
                                        Time: {meetingDateTime.startTime} to {meetingDateTime.endTime}
                                    </MeetingDetailLine>
                                </MeetingDetailsSection>
                            </div>
                            : <div>Meeting scheduled with {hostName || expert.name} - check meeting details for more information</div>)
                        : isPureAI
                            ? <div>This is an AI advisor — it answers you in the chat. Meetings are not available.</div>
                            : !canScheduleMeeting
                                ? <div>Meeting scheduling is currently not available for this adviser.</div>
                                : advisorType === 'ai-with-human'
                                    ? <div>No meeting scheduled yet. Meetings are with {hostName || 'the adviser'}, the person behind this AI agent.</div>
                                    : <div>No meeting scheduled yet. Please set up a meeting with this adviser.</div>
                    }
                </MeetingSection>
            </ExpertContract>

            {/* Marketplace consultation on this conversation: read it here once written; a locked one
                is unlocked on the site (payment / free quota live there). */}
            {consultation?.advice?.status === 'ready' && onReadConsultation && (
                <ButtonGroup>
                    <StyledButton
                        variant={ButtonVariant.SCHEDULE_OUTLINE}
                        size={ButtonSize.MD}
                        onClick={() => onReadConsultation(consultation)}
                        className="expert-card-read-consultation"
                    >
                        Read consultation
                    </StyledButton>
                </ButtonGroup>
            )}
            {consultation?.advice?.status === 'locked' && (
                <ConsultationHint>Written consultation not unlocked yet. Unlock it on imavenai.com (the link is in your email).</ConsultationHint>
            )}

            <ButtonGroup>
                {/* Only a person can be met: hidden for AI advisors and when the host turned meetings off */}
                {canScheduleMeeting && (
                    <StyledButton
                        variant={isMettingScheduled ? ButtonVariant.SCHEDULE_OUTLINE : ButtonVariant.SCHEDULE}
                        size={ButtonSize.MD}
                        onClick={() => onScheduleMeeting(expert)}
                        className={`expert-card-button angular-button ${isMettingScheduled ? 'meeting-info button-outline-primary' : 'setup-meeting button-translucent'}`}
                    >
                        {isMettingScheduled ? "Edit Meeting" : "Set Up Meeting"}
                    </StyledButton>
                )}
                <StyledButton
                    variant={ButtonVariant.CONFIRM}
                    size={ButtonSize.MD}
                    onClick={handleNavigateToChat}
                    aria-label={isPureAI ? "Open Chat" : "Go to Meeting"}
                    className="expert-card-meeting-btn"
                    style={!canScheduleMeeting ? { flex: 1 } : {}}
                >
                    {isPureAI ? "Open Chat" : "Go to Meeting"}
                </StyledButton>
            </ButtonGroup>
        </StyledCard>
    );
};

export default ExpertCardComponent;